import { useMemo, useState } from "react";
import { Shirt, Check } from "lucide-react";
import LoadMore from "../../components/LoadMore";

const parseISO = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d, 12); };
const prettyDate = (s) => { const d = parseISO(s); return d ? d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" }) : "No date"; };

const FILTERS = [
  { k: "to_fulfill", l: "To pack" },
  { k: "fulfilled", l: "Fulfilled" },
  { k: "all", l: "All" },
];

// aggregate size -> qty across a list of orders
function sizeBreakdown(orders) {
  const map = {};
  orders.forEach((o) => (o.items || []).forEach((t) => { if ((t.qty || 0) > 0) map[t.size] = (map[t.size] || 0) + t.qty; }));
  return Object.entries(map).sort((a, b) => b[1] - a[1]);
}

export default function AdminMerch({ ctx }) {
  const { merch, toggleMerch } = ctx;
  const [filter, setFilter] = useState("to_fulfill");

  const groups = useMemo(() => {
    const list = (merch || []).filter((o) => filter === "all" || o.status === filter);
    const byKey = {};
    list.forEach((o) => {
      const key = `${o.class_title || "Other"}||${o.class_date || ""}`;
      (byKey[key] = byKey[key] || { title: o.class_title || "Other", date: o.class_date || "", orders: [] }).orders.push(o);
    });
    return Object.values(byKey).sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));
  }, [merch, filter]);

  const totalShirts = (orders) => orders.reduce((s, o) => s + (o.items || []).reduce((x, t) => x + (t.qty || 0), 0), 0);

  return (
    <div className="mt-10 space-y-6" data-testid="admin-merch">
      <p className="text-sm text-stone-500">T-shirt add-on orders, grouped by class/camp so you can pack shirts per pickup day. Each is picked up by the customer at the studio.</p>

      <div className="flex gap-2" data-testid="merch-filter">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            data-testid={`merch-filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold transition-colors ${filter === f.k ? "bg-[#C6FF33]" : "bg-white hover:bg-[#F2E9D8]"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <p className="text-stone-500">No t-shirt orders {filter === "to_fulfill" ? "to pack" : filter === "fulfilled" ? "marked fulfilled" : "yet"}.</p>
      ) : (
        groups.map((g) => {
          const pending = g.orders.filter((o) => o.status !== "fulfilled").length;
          return (
            <div key={`${g.title}-${g.date}`} data-testid="merch-group" className="border-2 border-[#121212] bg-white">
              <div className="p-4 bg-[#F5EFE6] border-b-2 border-[#121212]">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-serif font-black text-xl text-[#121212]">{g.title}</p>
                    <p className="text-xs tracking-[0.1em] uppercase text-[#121212]/60 mt-0.5">{prettyDate(g.date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="inline-flex items-center gap-1.5 font-bold text-[#121212]"><Shirt size={15} /> {totalShirts(g.orders)} shirt{totalShirts(g.orders) !== 1 ? "s" : ""}</p>
                    {pending > 0 && <p className="text-xs text-orange-600 font-bold mt-0.5">{pending} to pack</p>}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="merch-size-breakdown">
                  {sizeBreakdown(g.orders).map(([size, qty]) => (
                    <span key={size} className="px-2.5 py-1 bg-[#121212] text-[#F2E9D8] text-xs font-bold">{qty}× {size}</span>
                  ))}
                </div>
              </div>

              <div className="divide-y divide-stone-100">
                {g.orders.map((o) => (
                  <div key={o.id} data-testid={`merch-order-${o.id}`} className="p-4 flex justify-between gap-4 flex-wrap items-start">
                    <div className="min-w-0">
                      <p className="font-medium text-stone-900">{o.customer_name || o.guest_name || "Guest"}</p>
                      <p className="text-xs text-stone-500">{o.customer_email || o.guest_email || ""}</p>
                      <p className="text-sm text-stone-700 mt-1">{(o.items || []).map((t) => `${t.qty}× ${t.size}`).join(", ")} · ${o.total}</p>
                    </div>
                    <button
                      data-testid={`merch-toggle-${o.id}`}
                      onClick={() => toggleMerch(o)}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs tracking-[0.15em] uppercase font-bold h-fit transition-colors ${
                        o.status === "fulfilled"
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-[#121212] text-[#F2E9D8] hover:bg-black"
                      }`}
                    >
                      {o.status === "fulfilled" ? <><Check size={13} /> Fulfilled</> : "Mark fulfilled"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
      <LoadMore ctx={ctx} pkey="merchorders" label="Load older orders" />
    </div>
  );
}
